import { computeEdge, EDGE_BAND_ORDER, edgeBand, marketOutlook } from "./edge.js";
import { mapStoredGame } from "./storedGame.js";

export function analyzeHistoryGame(game) {
  const edge = computeEdge(mapStoredGame(game));
  let edgeResult = null;

  // Gated on probability, not points: a gap with no integer inside it buys
  // exactly nothing (frozen -1.5 against a consensus of -1.1 is +0.4 pts and
  // no line value at all), and grading it files a coin flip under the flat band.
  if (edge.measurable && edge.lineValueProbability > 0 && game.ats_result) {
    edgeResult = game.ats_result === "push"
      ? "push"
      : game.ats_result === edge.side ? "win" : "loss";
  }

  const frozenFavorite = favoriteSide(Number(game.league_spread));
  const marketFavorite = favoriteSide(edge.market);

  return {
    ...game,
    edge,
    edge_result: edgeResult,
    favorite_flip: Boolean(frozenFavorite && marketFavorite && frozenFavorite !== marketFavorite),
  };
}

/**
 * The picks that count toward the pool for a week: the five the picker marked
 * submitted, or -- when none are marked -- the five earliest by kickoff. The
 * pool pays on five; a picker may hold a longer shortlist and trim it, so a
 * naive count over every pick over-credits wins and mis-scores the 5-0 bonus.
 */
export function poolScoringPicks(games) {
  const byWeek = new Map();
  for (const game of games) {
    if (!game.pick) continue;
    if (!byWeek.has(game.week)) byWeek.set(game.week, []);
    byWeek.get(game.week).push(game);
  }

  const chosen = [];
  for (const weekGames of byWeek.values()) {
    const submitted = weekGames.filter((game) => game.pick.submitted_at);
    const pool = (submitted.length ? submitted : weekGames)
      .slice()
      .sort((a, b) => new Date(a.kickoff_at) - new Date(b.kickoff_at))
      .slice(0, 5);
    chosen.push(...pool);
  }
  return chosen;
}

export function summarizeGames(games) {
  const gradedEdges = games.filter((game) => game.edge_result);
  const wins = gradedEdges.filter((game) => game.edge_result === "win").length;
  const losses = gradedEdges.filter((game) => game.edge_result === "loss").length;
  const pushes = gradedEdges.filter((game) => game.edge_result === "push").length;
  const predicted = gradedEdges
    .map((game) => game.edge?.winProbability)
    .filter((p) => p != null);

  const scoringGames = poolScoringPicks(games);
  const graded = scoringGames.filter((game) => game.pick.result);
  const pickWins = graded.filter((game) => game.pick.result === "win").length;
  const pickLosses = graded.filter((game) => game.pick.result === "loss").length;
  const pickPushes = graded.filter((game) => game.pick.result === "push").length;

  const weeks = [...new Set(scoringGames.map((game) => game.week))];
  const perfectWeeks = weeks.filter((week) => {
    const weekPicks = scoringGames.filter((game) => game.week === week);
    return weekPicks.length === 5 && weekPicks.every((game) => game.pick.result === "win");
  });
  // Every perfect week pays the +2 here. The one exception in the rules -- a
  // picker handed the Automatic for missing the deadline -- used to be read off
  // a late `submitted_at`, but that stamp is now set when the commissioner's
  // sheet arrives, always after the deadline, so it says nothing about
  // lateness. The standings PDF is the record for that case.
  const bonus = perfectWeeks.length * 2;

  return {
    wins,
    losses,
    pushes,
    sample: wins + losses,
    coverRate: wins + losses ? (wins / (wins + losses)) * 100 : null,
    // The edge report's predicted-vs-actual pair, over the SAME games: every
    // graded one, pushes included. A push is a non-win on both sides of the
    // comparison -- `winProbability` excludes it and the pool pays nothing for
    // a tie -- so `winRate` divides by all graded games where `coverRate`
    // leaves pushes out. `coverRate` stays as it was for its other readers.
    graded: gradedEdges.length,
    winRate: gradedEdges.length ? (wins / gradedEdges.length) * 100 : null,
    // `winProbability` is already in percentage points (`marketOutlook`).
    predictedWinRate: predicted.length
      ? predicted.reduce((sum, p) => sum + p, 0) / predicted.length
      : null,
    pickWins,
    pickLosses,
    pickPushes,
    pickCount: graded.length,
    poolPoints: pickWins + bonus,
    bonus,
    // Perfect weeks that paid nothing, so a 5-0 with no +2 beside it reads as
    // the rule rather than as a bug.
    favoriteFlips: games.filter((game) => game.favorite_flip).length,
  };
}

export function weeklySummaries(games, weeks) {
  return weeks.map((week) => ({
    week,
    ...summarizeGames(games.filter((game) => game.week === week)),
  }));
}

/**
 * Cover rate by how much probability the frozen line actually bought, in the
 * same units and thresholds the card bands on (`EDGE_BAND_ORDER`, in
 * percentage points). Bucketing by raw points contradicts the app's thesis --
 * a half-point across 3 and two points across nothing are not the same edge.
 *
 * Each band carries `wins`, `losses` and `pushes` (from `summarizeGames`)
 * beside `sample` and `coverRate` (wins + losses, pushes left out), and the
 * report card's predicted-vs-actual pair over every graded game in the band:
 * `graded`, `winRate` (a push counts as a non-win) and `predictedWinRate`
 * (the mean edge-side `winProbability`, in percentage points).
 */
export function edgeBandSummaries(games) {
  // One ladder, shared with the card — see `EDGE_BAND_ORDER`.
  // `floor` is the bucketing edge, `min` stays the band's published threshold.
  // They differ only for the bottom band, which has to catch a NEGATIVE line
  // value -- the market moved against the frozen number -- the same way
  // `edgeBand` floors one to "flat". Bucketing on `min` alone dropped those
  // games out of the report card entirely while the card still called them flat.
  const bands = EDGE_BAND_ORDER.map((band, i) => ({
    ...band,
    floor: i === 0 ? -Infinity : band.min,
    max: EDGE_BAND_ORDER[i + 1]?.min ?? Infinity,
  }));

  return bands.map((band) => ({
    ...band,
    ...summarizeGames(games.filter((game) => {
      const p = game.edge.lineValueProbability;
      return p != null && p >= band.floor && p < band.max;
    })),
  }));
}

/**
 * Below this many graded games a band's actual-vs-predicted colour is noise:
 * one Strong game going 0-1 against a 55% prediction says nothing, so the rate
 * renders neutral rather than red.
 */
export const MIN_BAND_GAMES_FOR_TONE = 5;

/**
 * How a band's actual rate is coloured: "good" when the edge side won at least as
 * often as the model said it would, "bad" below, "neutral" on too few games to
 * say, and null for an empty band (it shows —, uncoloured).
 */
export function bandOutcomeTone({ graded, winRate, predictedWinRate }) {
  if (!graded || winRate == null) return null;
  if (graded < MIN_BAND_GAMES_FOR_TONE || predictedWinRate == null) return "neutral";
  return winRate >= predictedWinRate ? "good" : "bad";
}

/**
 * Every band's graded games taken together, for the report card's
 * "All bands: predicted … · actual … · N games" line. Weighted by each band's
 * game count, so it is the same average `summarizeGames` would take over the
 * pooled games -- never a mean of band means.
 */
export function edgeBandTotals(bands) {
  const graded = bands.reduce((n, b) => n + (b.graded || 0), 0);
  const wins = bands.reduce((n, b) => n + (b.wins || 0), 0);
  const weighted = bands.filter((b) => b.graded && b.predictedWinRate != null);
  const predictedGames = weighted.reduce((n, b) => n + b.graded, 0);

  return {
    graded,
    wins,
    winRate: graded ? (wins / graded) * 100 : null,
    predictedWinRate: predictedGames
      ? weighted.reduce((sum, b) => sum + b.predictedWinRate * b.graded, 0) / predictedGames
      : null,
  };
}

/**
 * The picker's own graded picks, filed under the same line-value ladder as
 * `edgeBandSummaries`, so the report card can answer "am I taking the stale
 * numbers, and do they pay?"
 *
 * `poolWeeks` is `/history`'s `pool.weeks`: each row's `picks` carry the
 * commissioner's own verdicts (`{ team, abbr, result }`), and those verdicts
 * ARE the record here -- nothing is regraded from scores. A pick with no
 * verdict yet is left out entirely.
 *
 * The band is the line value of the side PICKED, read off `marketOutlook`'s
 * per-side figure -- never `edge.lineValueProbability` negated, which strands
 * the push term (CLAUDE.md: each side's line value is its own difference). A
 * pick against the edge side usually reads flat or below, and `edgeBand`
 * floors a negative value to flat exactly as the all-games bands do.
 *
 * A pick whose game has no frozen line, no deadline market, or is not on the
 * slate at all is `unrated`: counted, but in no band.
 *
 * Null when there is no graded pick at all -- an unlinked account, or a week
 * whose results have not landed -- so the card stays exactly as it was.
 */
export function yourPickBands(games, poolWeeks) {
  const bands = EDGE_BAND_ORDER.map(({ key, label }) => ({ key, label, wins: 0, losses: 0, pushes: 0 }));
  const byKey = new Map(bands.map((band) => [band.key, band]));
  let unrated = 0;
  let graded = 0;

  for (const row of poolWeeks || []) {
    for (const pick of row.picks || []) {
      if (!["win", "loss", "push"].includes(pick.result)) continue;
      graded += 1;

      const lineValue = pickedSideLineValue(findPickedGame(games, row.week, pick));
      if (lineValue == null) {
        unrated += 1;
        continue;
      }

      const band = byKey.get(edgeBand(lineValue));
      if (pick.result === "win") band.wins += 1;
      else if (pick.result === "loss") band.losses += 1;
      else band.pushes += 1;
    }
  }

  if (!graded) return null;

  return {
    bands: bands.map((band) => ({
      ...band,
      count: band.wins + band.losses + band.pushes,
      // Same shape as the all-games record: a push is neither right nor wrong.
      sample: band.wins + band.losses,
    })),
    unrated,
  };
}

export function findPickedGame(games, week, pick) {
  for (const game of games) {
    if (game.week !== week) continue;
    if ((pick.abbr && game.home_abbr === pick.abbr) || game.home_team === pick.team) return { game, side: "home" };
    if ((pick.abbr && game.away_abbr === pick.abbr) || game.away_team === pick.team) return { game, side: "away" };
  }
  return null;
}

function pickedSideLineValue(found) {
  if (!found) return null;
  const stored = mapStoredGame(found.game);
  if (stored.fivePicksSpread == null) return null;

  const outlook = marketOutlook(stored.spreads, stored.fivePicksSpread);
  if (!outlook) return null;
  return found.side === "home" ? outlook.homeLineValue : outlook.awayLineValue;
}

function favoriteSide(spread) {
  if (spread == null || Number.isNaN(spread) || spread === 0) return null;
  return spread < 0 ? "home" : "away";
}

/**
 * Order for the week's games list.
 *
 * Week always leads, whatever the key: a season-wide list sorted purely on
 * edge would interleave eighteen weeks into one run, and the week scope has
 * only one week in it anyway, so leading with it costs nothing there.
 *
 * A game with no line value has nothing to rank on -- no deadline market, or
 * no frozen line -- and it sinks to the bottom in BOTH directions. Letting
 * `null` fall through to a numeric compare sorts it as zero, which drops it
 * into the middle of the flat band as though the market had been measured and
 * found not to have moved. Those are different facts.
 */
export function compareGames({ key, desc }) {
  const flip = desc ? 1 : -1;
  const byKickoff = (a, b, dir) => (new Date(b.kickoff_at) - new Date(a.kickoff_at)) * dir;

  return (a, b) => {
    const byWeek = b.week - a.week;
    if (byWeek) return byWeek;

    if (key === "edge") {
      const av = a.edge.lineValueProbability;
      const bv = b.edge.lineValueProbability;
      if (av == null && bv == null) return byKickoff(a, b, 1);
      if (av == null) return 1;
      if (bv == null) return -1;
      return (bv - av) * flip || byKickoff(a, b, 1);
    }

    return byKickoff(a, b, flip);
  };
}

/**
 * The picker's week, off the sheet's own `[wins, losses, ties]`, for the
 * Results -> Week header: picks record, pool points, and whether the +2 was
 * paid.
 *
 * The commissioner folds the perfect-week bonus into the wins column, so a
 * five-for-five week arrives as 7-0 (CLAUDE.md, "The other inbound"). The
 * points are therefore the wins column as printed -- adding 2 again would
 * double-pay it -- and the record takes the bonus back out so it reads as
 * picks: 5-0, not 7-0. Null when the week is not graded.
 */
export function weekRecordSummary(record) {
  if (!Array.isArray(record) || record.length < 2) return null;
  const [wins, losses, ties = 0] = record;
  const perfect = wins === 7 && losses === 0 && !ties;
  const picksWon = perfect ? 5 : wins;
  return {
    label: ties ? `${picksWon}-${losses}-${ties}` : `${picksWon}-${losses}`,
    points: wins,
    perfect,
  };
}
